import i18next from "i18next";
import React, { useMemo } from 'react';
import { useCurrentUserLogin } from '../hooks/useCurrentUserLogin';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useGetCurrentUserTags } from "../api/hooks/useRiskThemes";
import { useAdmin } from "../context/adminContext";
import { useDesignSystem } from "../context/designSystemContext";
import { Categories } from "../types/categories";
import { ControlType } from "../types/controlsRisk";
import { canEditRisk as getCanEditRisk } from "../utils/canEditRisk";
import { pSBC } from "../utils/pSBC";
import { transformValue2Front } from "../utils/transformValue";
import { EditRiskButton } from "./editRiskThemeButton";

type riskThemeInformationProps = {
    riskTheme: Categories,
    controls: ControlType[],
    refetchRiskTheme: () => void
}

export const RiskThemeInformation: React.FC<riskThemeInformationProps> = ({ riskTheme, controls, refetchRiskTheme }) => {
    const isAdmin = useAdmin()
    const { designSystem } = useDesignSystem();
    const currentUserLogin = useCurrentUserLogin();

    const { data: currentUserTags } = useGetCurrentUserTags()

    // Funcao para contar os itens por status [{r/g}_{nome_status}] red ou green com o status
    const defineColor = (control: ControlType) => { 
        const currentDate = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
        const termDate = new Date(control.plannedFinishDate);
        let finalDate: Date | null = null;
        if (control.actualFinishDate) {
            finalDate = new Date(control.actualFinishDate);
        }

        if ((currentDate > termDate && !control.actualFinishDate) || (finalDate != null && termDate < finalDate)) {
            return 'r'; // Red
        } else {
            return 'g'; // Green
        }
    };

    const countByStatus = (list: ControlType[]) => {
        const initialStatus: Record<string, number> = {
            r_GOING: 0,
            g_GOING: 0,
            r_FINISHED: 0,
            g_FINISHED: 0,
            r_NOT_STARTED: 0,
            g_NOT_STARTED: 0
        };

        return list.reduce((acc: Record<string, number>, item) => {
            const colorPrefix = defineColor(item); // 'g' ou 'r'
            const statusKey = item.status.includes(" ") ? item.status.replace(/ /g, "_") : item.status;
            const key = `${colorPrefix}_${statusKey}`;

            // Initialize the key with 0 if it doesn't exist
            if (!acc[key]) {
                acc[key] = 0;
            }

            // Increment the count
            acc[key] += 1;

            return acc;
        }, { ...initialStatus });
    };

    const { status, combinedStatusCounts, maxStatus } = useMemo(() => {
        const countedStatus = countByStatus(controls);
        const combinedCounts = Object.keys(countedStatus).reduce((acc: any, key: string) => {
            const baseKey = key.split('_').slice(1).join('_');
            acc[baseKey] = (acc[baseKey] || 0) + countedStatus[key];
            return acc;
        }, {});
        const maxStat = Object.keys(combinedCounts).reduce((max, key) => {
            return combinedCounts[key] > combinedCounts[max] ? key : max;
        }, Object.keys(combinedCounts)[0]);

        return { status: countedStatus, combinedStatusCounts: combinedCounts, maxStatus: maxStat };
    }, [controls]); // Recalculate when controls change
    //console.log(status)
    // console.log(`O status com o maior nmero combinado é ${maxStatus} com ${combinedStatusCounts[maxStatus]} ocorrências.`);

    // Transform status into status_graph
    const status_graph = [
        {
            name: i18next.t("status_options_finished"),
            red: status["r_FINISHED"],
            green: status["g_FINISHED"],
        },
        {
            name: i18next.t("status_options_on_time"),
            red: status["r_GOING"],
            green: status["g_GOING"],
        },
        {
            name: i18next.t("status_options_not_started"),
            red: status["r_NOT_STARTED"],
            green: status["g_NOT_STARTED"],
        },
    ];

    const ColorLateralBar = (priority: string) => {
        //console.log(priority =="LOW")
        switch (priority) {
            case "HIGH":
                return "bg-red-300"
            case "REGULAR":
                return "bg-yellow-300"
            case "LOW":
                return "bg-green-300"
        }
    }

    const getRadius = (red: number, green: number, isRed: boolean) => {
        const borderRadius = 7
        if (isRed) {
            return green > 0 ? [borderRadius,borderRadius,0,0] : [borderRadius,borderRadius,borderRadius,borderRadius];
        } else {
            return red > 0 ? [0,0,borderRadius,borderRadius] : [borderRadius,borderRadius,borderRadius,borderRadius];
        }
    };

    const canEditRisk = getCanEditRisk({
        isAdmin: Boolean(isAdmin),
        riskTheme,
        currentUserLogin,
        currentUserTags,
    });
    
    /* ------------------------------------------------------------------------------------------------ */

    const itens2ColDiv = "flex gap-1 font-openSans text-[11.55px] leading-[21.33px]"
    const riskThemeInfo = "font-bold"
    return (
        <div className="md:flex gap-4">

            <div className="md:flex md:basis-2/3 border rounded-lg my-4 ml-4 gap-4 shadow-lg bg-white relative">
                <div className="absolute top-2 right-2 cursor-pointer">
                    {canEditRisk && <EditRiskButton riskTheme={riskTheme} refetchRiskTheme={refetchRiskTheme} />}
                </div>
                <span
                    className={`absolute left-0 top-0 bottom-0 w-[0.4rem] ${ColorLateralBar(riskTheme.priority)} rounded-l-lg`}
                ></span>
                
                <div className="basis-2/3 ml-6 mt-2.5">
                    <p className="font-bold font-openSans my-3 text-[16px] leading-[16px]" style={{ color: `${pSBC(0.9, designSystem?.cssBasics.primaryColor)}` }}>{i18next.t("risk_summary")}</p>
                    <p className="font-openSans text-[13.06px] leading-[24.11px]">{riskTheme.description}</p>
                </div>
                <div className="basis-1/3 pt-8 pb-3 ml-6 md:ml-0">
                    <div className={itens2ColDiv}>
                        <p>{i18next.t("category")}:</p>
                        <p className={riskThemeInfo}>{riskTheme.category}</p>
                    </div>
                    <div className={itens2ColDiv}>
                        <p>{i18next.t("subcategory")}: </p>
                        <p className={riskThemeInfo}>{riskTheme.subcategory}</p>
                    </div>
                    <div className={itens2ColDiv}>
                        <p className="whitespace-nowrap">{i18next.t("risk_theme")}: </p>
                        <p className={riskThemeInfo}>{riskTheme.theme}</p>
                    </div>
                    <div className={itens2ColDiv}>
                        <p>{i18next.t("severity")}: </p>
                        <p className={riskThemeInfo}>{transformValue2Front(riskTheme.severity, "severity")}</p>
                    </div>
                    <div className={itens2ColDiv}>
                        <p>{i18next.t("probability")}: </p>
                        <p className={riskThemeInfo}>{transformValue2Front(riskTheme.probability, "probability")}</p>
                    </div>
                    <div className={itens2ColDiv}>
                        <p>{i18next.t("priority")}: </p>
                        <p className={riskThemeInfo}>{transformValue2Front(riskTheme.priority, "priority")}</p>
                    </div>
                    <div className={itens2ColDiv}>
                        <p>{i18next.t("tags")}: </p>
                        <p className={riskThemeInfo}>{transformValue2Front(riskTheme.tags?.map(t => t.name)?.join(', ') || '-', "tags")}</p>
                    </div>
                    <div className={itens2ColDiv}>
                        <p>{i18next.t("risk_owner")}: </p>
                        <p className={riskThemeInfo}>{riskTheme.owner.name}</p>
                    </div>
                    {(riskTheme as any).managers?.length > 0 && (
                        <div className={itens2ColDiv}>
                            <p className="whitespace-nowrap flex-shrink-0">{i18next.t("risk_managers")}: </p>
                            <p className={riskThemeInfo + " break-words min-w-0"}>
                                {(riskTheme as any).managers
                                    .map((m: { name?: string; login?: string }) => m.name || m.login?.replace(/\b\w/g, (c: string) => c.toUpperCase()).split(".").join(" "))
                                    .join(", ")}
                            </p>
                        </div>
                    )}
                </div>
            </div>


            <div className="md:basis-1/3 border rounded-lg mr-4 my-4 shadow-lg bg-white">

                <p className="font-bold ml-5 mt-3.5 font-openSans text-[16px] leading-[16px]" style={{ color: `${pSBC(0.9, designSystem?.cssBasics.primaryColor)}` }}>
                    {i18next.t("implementation_status")}
                </p>

                <ResponsiveContainer width="100%" height={220}>
                    <BarChart
                        data={status_graph}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 5,
                        }}
                        barSize={60}
                    >
                        {/* <CartesianGrid strokeDasharray="3 3" /> */}
                        <XAxis dataKey="name" className="font-openSans text-sm" />
                        {/* <YAxis /> */}
                        <YAxis domain={[0, maxStatus]} hide />
                        <Tooltip />
                        {/* <Legend /> */}
                        {/* radius={getRadius(entry.red, entry.green, false)} */}
                        <Bar dataKey="green" name={i18next.t("implementation_status_on_time")} stackId="a" fill="#99FFCE">
                            {status_graph.map((entry: any, index: any) => (
                                <Cell key={`cell-green-${index}`}  /> 
                            ))}
                        </Bar>
                         {/* radius={getRadius(entry.red, entry.green, true)} */}
                        <Bar dataKey="red" name={i18next.t("implementation_status_late")} stackId="a" fill="#ff9999">
                            {status_graph.map((entry: any, index: any) => (
                                <Cell key={`cell-red-${index}`}  />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>

            </div>
        </div>
    )
}

